import { Trophy, Calendar, Loader2 } from "lucide-react";
import { useCandidateAwards } from "@/pages/candidate-profile/hooks/useCandidateProfile";
import type { AwardDetail } from "@/interface/award";

export function AwardsTab({
  profileId,
  awards = [],
}: {
  profileId?: number | string;
  awards?: AwardDetail[];
}) {
  const {
    data: awardsQuery,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useCandidateAwards(profileId, { page: 1, page_size: 12 });

  const queryAwards = awardsQuery?.pages?.flatMap((page: any) =>
    Array.isArray(page?.data) ? page.data : page?.data?.results || [],
  );

  const awardList: AwardDetail[] =
    queryAwards && queryAwards.length > 0
      ? queryAwards
      : Array.isArray(awards)
        ? awards
        : [];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col max-h-[750px]">
      <h2 className="text-lg font-bold text-primary mb-6 border-b border-slate-100 dark:border-slate-800 pb-4 shrink-0">
        Awards & Recognitions
      </h2>

      {isLoading && awardList.length === 0 ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="overflow-y-auto pr-1 flex-1 space-y-4"
        >
          {awardList.length === 0 ? (
            <div className="text-center p-8 text-slate-500 text-sm">
              No awards listed.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {awardList.map((award: AwardDetail, i: number) => {
                const title = award.award_title || "Award";
                const org = award.issuer || "Organization N/A";
                const date = award.award_date || "";

                return (
                  <div
                    key={award.id || i}
                    className="flex items-start gap-4 p-5 border border-slate-100 dark:border-slate-800 rounded-xl hover:border-amber-500/50 transition-colors"
                  >
                    <div className="h-12 w-12 bg-amber-500/10 rounded-xl flex items-center justify-center shrink-0 border border-amber-500/20">
                      <Trophy className="h-6 w-6 text-amber-500" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-slate-900 dark:text-white leading-tight">
                        {title}
                      </h4>
                      <p className="text-xs text-slate-500 mt-1.5">{org}</p>
                      {date && (
                        <p className="mt-1 text-xs font-medium text-slate-400 flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> {date}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="ml-2 text-xs text-slate-500 font-medium">
                Loading more awards...
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
